import { useEffect, useState, useCallback } from "react";
import api, { apiErrorMessage } from "../lib/api";
import { DAYS, mondayOf, isoDate, addDays, parseShiftTime } from "../lib/schedule";
import { useShifts } from "../context/ShiftsContext";
import { ChevronLeft, ChevronRight, Printer, Check, Clock, CalendarPlus, X, Settings2, UserPlus, Copy, Pencil } from "lucide-react";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const RANGES = [
  { key: "month", label: "Rest of this month" },
  { key: "4", label: "Next 4 weeks" },
  { key: "8", label: "Next 8 weeks" },
  { key: "12", label: "Next 12 weeks" },
];

function coverageMeta(n) {
  if (n === 0) return { cls: "bg-rose-100 text-rose-700 border-rose-200" };
  if (n === 1) return { cls: "bg-amber-100 text-amber-800 border-amber-200" };
  return { cls: "bg-emerald-100 text-emerald-800 border-emerald-200" };
}

function computeEndDate(startIso, rangeKey) {
  const start = new Date(startIso + "T00:00:00");
  if (rangeKey === "month") {
    const end = new Date(start.getFullYear(), start.getMonth() + 1, 0);
    return isoDate(end);
  }
  return isoDate(addDays(start, parseInt(rangeKey, 10) * 7));
}

export default function MasterSchedule({ user, isAdmin }) {
  const { shifts: SHIFTS, reload: reloadShifts } = useShifts();
  const [weekStart, setWeekStart] = useState(mondayOf(new Date()));
  const [cells, setCells] = useState({});
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ days: [], shift: "1st", start_date: isoDate(new Date()), range: "month" });
  const [shiftEditOpen, setShiftEditOpen] = useState(false);
  const [shiftDraft, setShiftDraft] = useState({});
  const weekIso = isoDate(weekStart);

  const openShiftEditor = () => {
    const d = {};
    SHIFTS.forEach((s) => { d[s.key] = { label: s.label, time: s.time, sub: s.sub }; });
    setShiftDraft(d);
    setShiftEditOpen(true);
  };

  const saveShiftTimes = async () => {
    try {
      await api.put("/shift-settings", { shifts: shiftDraft });
      await reloadShifts();
      toast.success("Shift times updated");
      setShiftEditOpen(false);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const [users, setUsers] = useState([]);
  const [assign, setAssign] = useState({ open: false, key: null, user_id: "", start_time: "", end_time: "" });

  useEffect(() => { if (isAdmin) api.get("/users").then((r) => setUsers(r.data)).catch(() => {}); }, [isAdmin]);

  const openAssign = (key) => {
    const shift = key.split("-")[1];
    const t = parseShiftTime((SHIFTS.find((s) => s.key === shift) || {}).time || "");
    setAssign({ open: true, key, user_id: "", start_time: t.start, end_time: t.end });
  };

  const submitAssign = async () => {
    if (!assign.user_id) return toast.error("Choose a staff member");
    const [day, shift] = assign.key.split("-");
    try {
      await api.post("/schedule/cell/add-staff", { week_start: weekIso, day, shift, user_id: assign.user_id, start_time: assign.start_time, end_time: assign.end_time });
      toast.success("Staff hours added");
      setAssign((a) => ({ ...a, open: false }));
      load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const removeStaff = async (key, staffId) => {
    const [day, shift] = key.split("-");
    try {
      await api.post("/schedule/cell/remove-one", { week_start: weekIso, day, shift, user_id: staffId });
      load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const [copyOpen, setCopyOpen] = useState(false);
  const [copyCount, setCopyCount] = useState("1");
  const [myHours, setMyHours] = useState({ open: false, key: null, start_time: "", end_time: "" });

  const copyWeek = async () => {
    try {
      const res = await api.post("/schedule/copy-week", { from_week: weekIso, count: parseInt(copyCount, 10) });
      if (!res.data.weeks) return toast.error("This week has nothing to copy yet");
      toast.success(`Copied schedule to ${res.data.weeks} upcoming week${res.data.weeks === 1 ? "" : "s"}`);
      setCopyOpen(false);
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const openMyHours = (key, st) => {
    const shift = key.split("-")[1];
    const t = parseShiftTime((SHIFTS.find((s) => s.key === shift) || {}).time || "");
    setMyHours({ open: true, key, start_time: st.start_time || t.start, end_time: st.end_time || t.end });
  };

  const submitMyHours = async () => {
    const [day, shift] = myHours.key.split("-");
    try {
      await api.post("/schedule/cell/update-my-hours", { week_start: weekIso, day, shift, start_time: myHours.start_time, end_time: myHours.end_time });
      toast.success("Your hours updated");
      setMyHours((m) => ({ ...m, open: false }));
      load();
    } catch (err) { toast.error(apiErrorMessage(err)); }
  };

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/schedule?week_start=${weekIso}`);
      setCells(res.data);
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  }, [weekIso]);

  useEffect(() => { load(); }, [load]);

  const startEdit = (key) => {
    if (!isAdmin) return;
    setEditing(key);
    setDraft(cells[key]?.text || "");
  };

  const saveCell = async (key) => {
    const [day, shift] = key.split("-");
    try {
      await api.put("/schedule/cell", { week_start: weekIso, day, shift, text: draft, staff: cells[key]?.staff || [] });
      setCells((c) => ({ ...c, [key]: { ...(c[key] || {}), text: draft } }));
      setEditing(null);
      toast.success("Schedule updated");
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const removeSelf = async (key) => {
    const [day, shift] = key.split("-");
    try {
      await api.post("/schedule/cell/remove-staff", { week_start: weekIso, day, shift });
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const toggleDay = (d) =>
    setForm((f) => ({ ...f, days: f.days.includes(d) ? f.days.filter((x) => x !== d) : [...f.days, d] }));

  const submitRecurring = async () => {
    if (form.days.length === 0) return toast.error("Pick at least one weekday");
    const end_date = computeEndDate(form.start_date, form.range);
    try {
      const res = await api.post("/schedule/recurring", { days: form.days, shift: form.shift, start_date: form.start_date, end_date });
      toast.success(`Added to ${res.data.cells_updated} shift${res.data.cells_updated === 1 ? "" : "s"}`);
      setAddOpen(false);
      setForm({ days: [], shift: "1st", start_date: isoDate(new Date()), range: "month" });
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    }
  };

  const weekLabel = `${weekStart.toLocaleDateString("en-US", { month: "short", day: "numeric" })} – ${addDays(weekStart, 6).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-display">Master Schedule</h1>
          <p className="text-sm text-slate-500 mt-1">{isAdmin ? "Click any cell to edit — or add your regular shifts to auto-fill weeks." : "Your department's weekly coverage. Add your regular shifts to auto-fill."}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isAdmin && (
            <Dialog open={shiftEditOpen} onOpenChange={setShiftEditOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" onClick={openShiftEditor} data-testid="edit-shift-times-button" className="gap-2"><Settings2 className="h-4 w-4" />Shift Times</Button>
              </DialogTrigger>
              <DialogContent data-testid="edit-shift-times-dialog">
                <DialogHeader><DialogTitle>Edit shift times</DialogTitle></DialogHeader>
                <div className="space-y-4 py-2">
                  {SHIFTS.map((s) => (
                    <div key={s.key} className="rounded-lg border border-slate-200 p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.dot }} />
                        <span className="text-sm font-semibold text-slate-700">{s.key === "1st" ? "1st" : s.key === "2nd" ? "2nd" : "3rd"} Shift</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-xs">Name</Label>
                          <Input value={shiftDraft[s.key]?.label || ""} data-testid={`shift-label-input-${s.key}`}
                            onChange={(e) => setShiftDraft((d) => ({ ...d, [s.key]: { ...d[s.key], label: e.target.value } }))} />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Time</Label>
                          <Input value={shiftDraft[s.key]?.time || ""} data-testid={`shift-time-input-${s.key}`}
                            placeholder="07:00 – 15:30"
                            onChange={(e) => setShiftDraft((d) => ({ ...d, [s.key]: { ...d[s.key], time: e.target.value } }))} />
                        </div>
                      </div>
                      <div className="space-y-1 mt-2">
                        <Label className="text-xs">Description</Label>
                        <Input value={shiftDraft[s.key]?.sub || ""} data-testid={`shift-sub-input-${s.key}`}
                          onChange={(e) => setShiftDraft((d) => ({ ...d, [s.key]: { ...d[s.key], sub: e.target.value } }))} />
                      </div>
                    </div>
                  ))}
                </div>
                <DialogFooter><Button onClick={saveShiftTimes} data-testid="save-shift-times-button" className="bg-slate-900 hover:bg-slate-800">Save Shift Times</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          {isAdmin && (
            <Dialog open={copyOpen} onOpenChange={setCopyOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" onClick={() => setCopyOpen(true)} data-testid="copy-week-button" className="gap-2"><Copy className="h-4 w-4" />Copy Week</Button>
              </DialogTrigger>
              <DialogContent data-testid="copy-week-dialog">
                <DialogHeader><DialogTitle>Copy this week forward</DialogTitle></DialogHeader>
                <p className="text-sm text-slate-500 -mt-1">Copy this week's assignments and notes into upcoming weeks. Existing entries in those weeks are overwritten.</p>
                <div className="space-y-2 py-2">
                  <Label>Apply to the next…</Label>
                  <Select value={copyCount} onValueChange={setCopyCount}>
                    <SelectTrigger data-testid="copy-week-count-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{[1, 2, 3, 4, 6, 8, 12].map((n) => <SelectItem key={n} value={String(n)}>{n} week{n === 1 ? "" : "s"}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <DialogFooter><Button onClick={copyWeek} data-testid="submit-copy-week-button" className="bg-slate-900 hover:bg-slate-800">Copy Forward</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          )}
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-schedule-button" className="gap-2 bg-emerald-600 hover:bg-emerald-700"><CalendarPlus className="h-4 w-4" />Add Schedule</Button>
            </DialogTrigger>
            <DialogContent data-testid="add-schedule-dialog">
              <DialogHeader><DialogTitle>Add your regular shifts</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-500 -mt-1">Pick the days and shift you normally work. We'll auto-populate the schedule for you.</p>
              <div className="space-y-4 py-2">
                <div className="space-y-2">
                  <Label>Which days?</Label>
                  <div className="flex flex-wrap gap-2">
                    {DAYS.map((d) => {
                      const on = form.days.includes(d.key);
                      return (
                        <button key={d.key} type="button" onClick={() => toggleDay(d.key)}
                          data-testid={`recurring-day-${d.key}`}
                          className={`h-10 w-12 rounded-lg border text-sm font-semibold transition-all ${on ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
                          {d.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Shift</Label>
                  <Select value={form.shift} onValueChange={(v) => setForm({ ...form, shift: v })}>
                    <SelectTrigger data-testid="recurring-shift-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SHIFTS.map((s) => <SelectItem key={s.key} value={s.key}>{s.label} · {s.time}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Start date</Label>
                    <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="recurring-start-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Repeat for</Label>
                    <Select value={form.range} onValueChange={(v) => setForm({ ...form, range: v })}>
                      <SelectTrigger data-testid="recurring-range-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{RANGES.map((r) => <SelectItem key={r.key} value={r.key}>{r.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter><Button onClick={submitRecurring} data-testid="submit-recurring-button" className="bg-emerald-600 hover:bg-emerald-700">Auto-populate</Button></DialogFooter>
            </DialogContent>
          </Dialog>
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, -7))} data-testid="prev-week-button"><ChevronLeft className="h-4 w-4" /></Button>
          <div className="px-4 py-2 rounded-lg bg-white border border-slate-200 text-sm font-semibold text-slate-800 min-w-[210px] text-center" data-testid="week-label">{weekLabel}</div>
          <Button variant="outline" size="icon" onClick={() => setWeekStart(addDays(weekStart, 7))} data-testid="next-week-button"><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" onClick={() => window.print()} data-testid="print-schedule-button" className="gap-2"><Printer className="h-4 w-4" />Print</Button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap gap-2">
        {SHIFTS.map((s) => (
          <div key={s.key} className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-semibold ${s.badge}`}>
            <span className="h-2 w-2 rounded-full" style={{ background: s.dot }} />
            {s.label} · {s.time}
          </div>
        ))}
      </div>

      <div className="mb-5 flex flex-wrap items-center gap-3 text-xs text-slate-500">
        <span className="font-semibold text-slate-600 uppercase tracking-wider">Coverage</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-rose-400" />Uncovered</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-amber-400" />Thin (1 tech)</span>
        <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />Covered (2+)</span>
      </div>

      <div className="border border-slate-200 rounded-xl bg-white shadow-sm overflow-x-auto">
        <table className="w-full border-collapse text-left min-w-[1000px]">
          <thead>
            <tr>
              <th className="p-3 border-b border-r border-slate-200 bg-slate-50 w-40 text-xs font-semibold uppercase tracking-wider text-slate-500 sticky left-0 z-10">Shift</th>
              {DAYS.map((d, i) => (
                <th key={d.key} className="p-3 border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-700">
                  <div>{d.label}</div>
                  <div className="text-[11px] font-normal text-slate-400">{addDays(weekStart, i).toLocaleDateString("en-US", { month: "short", day: "numeric" })}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SHIFTS.map((s) => (
              <tr key={s.key}>
                <td className="p-3 border-b border-r border-slate-200 align-top sticky left-0 z-10 bg-white">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: s.dot }} />
                    <span className="font-semibold text-slate-800 text-sm">{s.label}</span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-1"><Clock className="h-3 w-3" />{s.time}</div>
                  <div className="text-[11px] text-slate-400">{s.sub}</div>
                </td>
                {DAYS.map((d) => {
                  const key = `${d.key}-${s.key}`;
                  const cell = cells[key];
                  const isEditing = editing === key;
                  const staff = cell?.staff || [];
                  return (
                    <td key={key}
                      data-testid={`schedule-cell-${key}`}
                      onClick={() => !isEditing && startEdit(key)}
                      className={`relative p-2 border-b border-slate-100 align-top min-h-[80px] transition-all ${isAdmin ? "cursor-text hover:bg-slate-50" : ""} ${isEditing ? "bg-sky-50 ring-2 ring-inset ring-sky-500" : ""}`}
                    >
                      {!isEditing && staff.length > 0 && (
                        <span data-testid={`cell-count-${key}`}
                          className={`absolute top-1 right-1 text-[10px] font-bold h-5 min-w-[20px] px-1 inline-flex items-center justify-center rounded-full border ${coverageMeta(staff.length).cls}`}>
                          {staff.length}
                        </span>
                      )}
                      {isEditing ? (
                        <div>
                          <textarea
                            autoFocus
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onBlur={() => saveCell(key)}
                            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) saveCell(key); if (e.key === "Escape") setEditing(null); }}
                            data-testid={`schedule-cell-input-${key}`}
                            rows={2}
                            className="w-full text-sm bg-transparent resize-none outline-none text-slate-800"
                            placeholder="Notes…"
                          />
                          <button onMouseDown={(e) => { e.preventDefault(); saveCell(key); }} data-testid={`schedule-cell-save-${key}`} className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-sky-700">
                            <Check className="h-3 w-3" /> Save
                          </button>
                        </div>
                      ) : (
                        <div className="min-h-[44px] space-y-1.5">
                          {cell?.text && <div className="text-sm text-slate-800 whitespace-pre-wrap leading-snug">{cell.text}</div>}
                          {staff.length > 0 && (
                            <div className="flex flex-wrap gap-1">
                              {staff.map((st) => {
                                const canRemove = st.id === user.id || isAdmin;
                                return (
                                  <span key={st.id} data-testid={`cell-staff-${key}-${st.id}`}
                                    className={`inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[11px] font-semibold border ${s.badge}`}>
                                    <span>{st.name}{(st.start_time && st.end_time) ? ` · ${st.start_time}-${st.end_time}` : ""}</span>
                                    {st.id === user.id && (
                                      <button onClick={(e) => { e.stopPropagation(); openMyHours(key, st); }}
                                        data-testid={`cell-staff-edit-${key}`}
                                        className="rounded-full hover:bg-black/10 p-0.5"><Pencil className="h-3 w-3" /></button>
                                    )}
                                    {canRemove && (
                                      <button onClick={(e) => { e.stopPropagation(); st.id === user.id ? removeSelf(key) : removeStaff(key, st.id); }}
                                        data-testid={`cell-staff-remove-${key}-${st.id}`}
                                        className="rounded-full hover:bg-black/10 p-0.5"><X className="h-3 w-3" /></button>
                                    )}
                                  </span>
                                );
                              })}
                            </div>
                          )}
                          {isAdmin && (
                            <button onClick={(e) => { e.stopPropagation(); openAssign(key); }}
                              data-testid={`assign-hours-button-${key}`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-slate-400 hover:text-slate-700">
                              <UserPlus className="h-3 w-3" />Hours
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td className="p-3 border-t-2 border-slate-200 bg-slate-50 sticky left-0 z-10 text-xs font-semibold uppercase tracking-wider text-slate-500">Techs / day</td>
              {DAYS.map((d) => {
                const total = SHIFTS.reduce((sum, s) => sum + ((cells[`${d.key}-${s.key}`]?.staff || []).length), 0);
                const meta = coverageMeta(total);
                return (
                  <td key={d.key} className="p-3 border-t-2 border-slate-200 bg-slate-50 text-center">
                    <span data-testid={`day-total-${d.key}`} className={`inline-flex items-center justify-center text-xs font-bold h-6 min-w-[24px] px-2 rounded-full border ${meta.cls}`}>{total}</span>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        </table>
      </div>

      <Dialog open={assign.open} onOpenChange={(v) => setAssign((a) => ({ ...a, open: v }))}>
        <DialogContent data-testid="assign-hours-dialog">
          <DialogHeader><DialogTitle>Add staff hours</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">Assign a staff member to this shift with their own variable hours.</p>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Staff member</Label>
              <Select value={assign.user_id} onValueChange={(v) => setAssign((a) => ({ ...a, user_id: v }))}>
                <SelectTrigger data-testid="assign-staff-select"><SelectValue placeholder="Choose staff" /></SelectTrigger>
                <SelectContent>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{u.title ? ` · ${u.title}` : ""}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>From</Label>
                <Input type="time" value={assign.start_time} onChange={(e) => setAssign((a) => ({ ...a, start_time: e.target.value }))} data-testid="assign-start-time-input" /></div>
              <div className="space-y-2"><Label>To</Label>
                <Input type="time" value={assign.end_time} onChange={(e) => setAssign((a) => ({ ...a, end_time: e.target.value }))} data-testid="assign-end-time-input" /></div>
            </div>
          </div>
          <DialogFooter><Button onClick={submitAssign} data-testid="submit-assign-hours-button" className="bg-slate-900 hover:bg-slate-800">Add to Schedule</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={myHours.open} onOpenChange={(v) => setMyHours((m) => ({ ...m, open: v }))}>
        <DialogContent data-testid="my-hours-dialog">
          <DialogHeader><DialogTitle>Adjust my hours</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-500 -mt-1">Fine-tune your working hours for this shift.</p>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="space-y-2"><Label>From</Label>
              <Input type="time" value={myHours.start_time} onChange={(e) => setMyHours((m) => ({ ...m, start_time: e.target.value }))} data-testid="my-hours-start-input" /></div>
            <div className="space-y-2"><Label>To</Label>
              <Input type="time" value={myHours.end_time} onChange={(e) => setMyHours((m) => ({ ...m, end_time: e.target.value }))} data-testid="my-hours-end-input" /></div>
          </div>
          <DialogFooter><Button onClick={submitMyHours} data-testid="submit-my-hours-button" className="bg-slate-900 hover:bg-slate-800">Save Hours</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
